import DatePicker from '@/components/Basic/DatePicker';
import Dialog from '@/components/Basic/Dialog';
import { useNotification } from '@/contexts/NotificationContext';
import type { SetSubscriptionEndDateVariables } from '@/hooks/use-subscriptions';
import type { SubscriptionType } from '@/types/types';
import {
  conflictMessage,
  getApiErrorMessage,
  getSubscriptionTypeLabel,
  isConflictError
} from '@/utils/utils';
import { editSubscriptionEndDateSchema } from '@/utils/validationSchemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, TextField, Typography } from '@mui/material';
import type { UseMutationResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';

export interface EditableSubscription {
  id: string;
  customerId: string;
  customerName: string;
  type: SubscriptionType;
  endDate: string;
  // REQ-4/REQ-9: this subscription's own version, sent back on save; a stale one answers 409. The
  // caller (SubscriptionsPage) re-derives this row from its own list by id on every render, so a
  // background reload after a conflict is reflected here without remounting this dialog.
  version: number;
}

interface EditSubscriptionEndDateDialogProps {
  subscription: EditableSubscription;
  setEndDate: UseMutationResult<void, Error, SetSubscriptionEndDateVariables>;
  onClose: () => void;
}

type EditEndDateFormData = z.infer<typeof editSubscriptionEndDateSchema>;

// Compliance follow-up (M4): the reason is a free-text field stored on the audit log
// (apps/api AuditLogEntry) — staff must be told not to put a customer's own personal data in it.
// Not exported: e2e specs never import application source (they drive the app as a black box
// through the browser, like every other spec in e2e/), so its own assertion matches a distinctive
// substring instead.
const REASON_HINT =
  'Short business reason, for example "paid 30 EUR for 3 more months". Do not enter personal data (names, phone numbers, addresses).';

// REQ-9/REQ-10: the administrator's only subscription write — any date, past or future, with a
// required reason (apps/ui/README.md § Subscriptions). The reason is never optional: the API
// itself rejects a blank one (400), and this form refuses to submit before reaching it. Opened
// only from SubscriptionsPage today (the customer detail page shows end dates read-only), called
// with the mutation already created by the caller's own useSubscriptions(), the same pattern
// RejectReceiptInboxDialog uses.
export const EditSubscriptionEndDateDialog: FC<EditSubscriptionEndDateDialogProps> = ({
  subscription,
  setEndDate,
  onClose
}) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const { control, handleSubmit } = useForm<EditEndDateFormData>({
    resolver: zodResolver(editSubscriptionEndDateSchema),
    defaultValues: { endDate: dayjs(subscription.endDate), reason: '' }
  });

  const onValid = (data: EditEndDateFormData) => {
    setEndDate.mutate(
      {
        id: subscription.id,
        customerId: subscription.customerId,
        request: {
          // A plain calendar day, "yyyy-MM-dd", no time or zone — the same convention apps/api's
          // SubscriptionApi#setEndDate test helper documents for this Calendar-typed field.
          endDate: data.endDate.format('YYYY-MM-DD'),
          reason: data.reason,
          // Always the subscription prop's own live value, never a draft frozen at mount — this is
          // what makes a retry after a 409 use the version the background reload just brought in
          // (REQ-9).
          version: subscription.version
        }
      },
      {
        onSuccess: () => {
          showSuccess('Subscription end date updated');
          onClose();
        },
        // REQ-9: on a 409 the hook itself already reloads this subscription in the background
        // (use-subscriptions.ts) — the dialog stays open with what was typed (nothing here clears
        // it), so the next Save re-sends it with the now-current version and succeeds.
        onError: err => {
          if (isConflictError(err)) {
            showWarning(conflictMessage('subscription'));
          } else {
            showError(getApiErrorMessage(err, 'Could not update the end date'));
          }
        }
      }
    );
  };

  return (
    <Dialog
      title="Edit subscription end date"
      open
      onClose={onClose}
      actions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={setEndDate.isPending} onClick={handleSubmit(onValid)}>
            {setEndDate.isPending ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {subscription.customerName} — {getSubscriptionTypeLabel(subscription.type)}
        </Typography>
        <Controller
          control={control}
          name="endDate"
          render={({ field, fieldState }) => (
            <DatePicker
              label="End date"
              value={field.value}
              onChange={date => field.onChange(date)}
              required
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="reason"
          render={({ field, fieldState }) => (
            <TextField
              label="Reason"
              fullWidth
              multiline
              minRows={3}
              required
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? REASON_HINT}
            />
          )}
        />
      </Box>
    </Dialog>
  );
};
